import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, fromEvent, map, Observable, take, takeUntil, throwError, timeout } from 'rxjs';
import { ChatMessage } from '../models/chat-message';

type OpenAiContentPart = { type: 'text'; text: string } | { type: 'image_url'; image_url: { url: string } };

type OpenAiMessage = {
  role: 'system' | 'user' | 'assistant';
  content: string | OpenAiContentPart[];
};

function toOpenAiContent(message: ChatMessage): string | OpenAiContentPart[] {
  const attachments = message.attachments ?? [];
  const images = attachments.filter((attachment) => attachment.dataUrl);
  const files = attachments.filter((attachment) => !attachment.dataUrl && attachment.text);
  if (images.length === 0 && files.length === 0) return message.text;
  const parts: OpenAiContentPart[] = [];
  if (message.text.trim().length > 0) parts.push({ type: 'text', text: message.text });
  for (const attachment of images) {
    parts.push({ type: 'image_url', image_url: { url: attachment.dataUrl! } });
  }
  for (const attachment of files) {
    parts.push({ type: 'text', text: `Attached file “${attachment.name}”:\n${attachment.text}` });
  }
  return parts;
}
import { ChatPersistenceService, DEFAULT_SETTINGS } from './chat-persistence.service';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

type LmStudioResponse = {
  choices?: Array<{
    message?: {
      content?: string;
      reasoning?: string;
      reasoning_content?: string;
    };
    finish_reason?: string;
  }>;
};

export type LmStudioReply = {
  text: string;
  thinking?: string;
};

export class LmStudioOutputLimitError extends Error {
  constructor() { super('The model reached its output limit before completing the response.'); }
}

type LmStudioStreamDelta = {
  choices?: Array<{
    delta?: {
      content?: string;
      reasoning?: string;
      reasoning_content?: string;
    };
  }>;
};

export type ChatOptions = {
  systemPrompt?: string;
  maxTokens?: number;
  reasoningEffort?: 'low' | 'medium' | 'high';
  /** Uses a specific model without changing the user's main-model selection. */
  model?: string;
  /** Intended for lightweight calls that must not delay the main request indefinitely. */
  timeoutMs?: number;
  /** Sends this request to the separately configured helper API, when present. */
  useHelperApi?: boolean;
};

type LmStudioModelsResponse = {
  data?: Array<{ id: string }>;
};

type ApiTarget = {
  baseUrl: string;
  apiKey?: string;
  modelIds: string[];
  proxyOpenAi: boolean;
};

const DEFAULT_SYSTEM_PROMPT = 'You are Serg, a thoughtful personal thinking partner. Be direct, but give answers that are thorough enough to be useful: explain the reasoning or describe the details when the question calls for it, rather than stopping at a single short sentence. Do not fabricate information; if the provided context is insufficient or unclear, state uncertainty and do not make up details.';

const IMAGE_ANALYSIS_PROMPT = 'When the user attaches an image, analyze it in detail before answering: describe the subject, shapes, colors, layout, and any text or other notable elements that are actually visible. Ground every claim in what the image shows, and only then answer the user\'s question.';

function systemPromptFor(messages: ChatMessage[], options: ChatOptions): string {
  const base = options.systemPrompt ?? DEFAULT_SYSTEM_PROMPT;
  // Structured extraction supplies its own image instructions and JSON contract.
  if (options.systemPrompt) return base;
  const hasImage = messages.some((message) => (message.attachments ?? []).some((attachment) => attachment.dataUrl));
  return hasImage ? `${base}\n\n${IMAGE_ANALYSIS_PROMPT}` : base;
}

@Injectable({ providedIn: 'root' })
export class LmStudioService {
  private readonly http = inject(HttpClient);
  private readonly persistence = inject(ChatPersistenceService);
  private readonly auth = inject(AuthService);
  async chat(messages: ChatMessage[], options: ChatOptions = {}): Promise<string> {
    return (await this.chatWithReasoning(messages, options)).text;
  }

  async chatWithVision(messages: ChatMessage[], options: ChatOptions = {}, signal?: AbortSignal): Promise<string> {
    const target = await this.resolveTarget(options, signal);
    return (await this.chatWithTarget(target, messages, options, signal)).text;
  }

  /** Returns the normal reply plus any reasoning explicitly included by the local model. */
  async chatWithReasoning(messages: ChatMessage[], options: ChatOptions = {}, signal?: AbortSignal): Promise<LmStudioReply> {
    const target = await this.resolveTarget(options, signal);
    return this.chatWithTarget(target, messages, options, signal);
  }

  async listModels(apiUrl?: string): Promise<string[]> {
    const isOpenAi = this.isOpenAiUrl(apiUrl ?? DEFAULT_SETTINGS.mainApiUrl);
    const target = await this.resolveTargetForApi(
      apiUrl,
      isOpenAi && this.auth.isConfigured ? '' : environment.apiKey,
      undefined,
      isOpenAi && this.auth.isConfigured,
    );
    return target.modelIds;
  }

  /** Returns the configured helper-model id for lightweight tasks, or undefined to fall back to the main model. */
  helperModelId(): string | undefined {
    const helper = this.persistence.getEffectiveSettings().helperModel;
    return helper.enabled ? helper.modelId?.trim() || this.mainModelId() : undefined;
  }

  /** Returns the configured main-model id (used to write the visible answer), for logging. */
  mainModelId(): string {
    return this.persistence.getEffectiveSettings().model || DEFAULT_SETTINGS.model;
  }

  /** Streams model-provided answer and reasoning deltas through the OpenAI-compatible SSE endpoint. */
  async streamWithReasoning(
    messages: ChatMessage[],
    options: ChatOptions,
    onUpdate: (reply: LmStudioReply) => void,
    signal?: AbortSignal,
  ): Promise<LmStudioReply> {
    const target = await this.resolveTarget(options, signal);
    const update = (reply: LmStudioReply) => {
      onUpdate(reply);
    };

    return this.streamWithTarget(target, messages, options, update, signal);
  }

  private async chatWithTarget(
    target: ApiTarget,
    messages: ChatMessage[],
    options: ChatOptions,
    signal?: AbortSignal,
  ): Promise<LmStudioReply> {
    const settings = this.persistence.getSettings();
    const endpoint = this.endpoint(target.baseUrl, 'chat/completions');
    const model = this.modelForTarget(target, options.model || settings?.model || DEFAULT_SETTINGS.model);
    const temperature = settings?.temperature ?? DEFAULT_SETTINGS.temperature;
    const reasoningEffort = options.reasoningEffort ?? settings?.reasoningEffort ?? DEFAULT_SETTINGS.reasoningEffort;
    const request = {
      model,
      messages: [
        {
          role: 'system',
          content: systemPromptFor(messages, options),
        },
        ...messages.map((message) => ({
          role: message.author === 'bot' ? 'assistant' : 'user',
          content: toOpenAiContent(message),
        } as OpenAiMessage)),
      ],
      ...this.generationOptions(target, model, temperature, options.maxTokens ?? 2048),
      stream: false,
      ...(this.supportsReasoningEffort(model) ? { reasoning_effort: reasoningEffort } : {}),
    };

    if (settings?.logLlmRequests ?? DEFAULT_SETTINGS.logLlmRequests) {
      console.groupCollapsed(`[Serg LLM request] ${model} → ${endpoint}`);
      console.log('Request payload:', request);
      console.groupEnd();
    }

    const response: LmStudioResponse = target.proxyOpenAi
      ? await this.proxyJson<LmStudioResponse>('chat/completions', request, signal, options.timeoutMs)
      : await firstValueFrom(
        options.timeoutMs
          ? this.withAbort(this.http.post<LmStudioResponse>(endpoint, request, { headers: this.headersFor(target) }), signal).pipe(timeout(options.timeoutMs))
          : this.withAbort(this.http.post<LmStudioResponse>(endpoint, request, { headers: this.headersFor(target) }), signal),
      );

    const choice = response.choices?.[0];
    if (choice?.finish_reason === 'length') throw new LmStudioOutputLimitError();
    const content = choice?.message?.content?.trim();
    if (!content) {
      const reason = choice?.finish_reason ? ` (finish reason: ${choice.finish_reason})` : '';
      throw new Error(`LM Studio returned an empty response${reason}.`);
    }
    const { text, thinking } = this.separateThinking(content, choice?.message?.reasoning_content ?? choice?.message?.reasoning);
    if (!text) {
      throw new Error('LM Studio returned reasoning without a final response.');
    }
    if (settings?.logLlmRequests ?? DEFAULT_SETTINGS.logLlmRequests) {
      console.log('[Serg LLM response]', { model, endpoint, finishReason: choice?.finish_reason, content: text, thinking });
    }
    return thinking ? { text, thinking } : { text };
  }

  private separateThinking(content: string, providedThinking?: string): LmStudioReply {
    const openingTag = content.match(/^\s*<(?:think|analysis)>\s*/i);
    const completedBlock = content.match(/^\s*<(think|analysis)>\s*([\s\S]*?)\s*<\/\1>\s*/i);
    if (!openingTag) return { text: content, thinking: providedThinking?.trim() || undefined };

    // During streaming, surface the contents as soon as the opening tag arrives;
    // the final answer is empty until the closing tag is received.
    const inlineThinking = completedBlock
      ? completedBlock[2].trim()
      : content.slice(openingTag[0].length).trim();
    return {
      text: completedBlock ? content.slice(completedBlock[0].length).trim() : '',
      thinking: providedThinking?.trim() || inlineThinking || undefined,
    };
  }

  private async streamWithTarget(
    target: ApiTarget,
    messages: ChatMessage[],
    options: ChatOptions,
    onUpdate: (reply: LmStudioReply) => void,
    signal?: AbortSignal,
  ): Promise<LmStudioReply> {
    const settings = this.persistence.getSettings();
    const endpoint = this.endpoint(target.baseUrl, 'chat/completions');
    const model = this.modelForTarget(target, options.model || settings?.model || DEFAULT_SETTINGS.model);
    const response = target.proxyOpenAi ? await this.proxyFetch('chat/completions', {
      model,
      messages: [
        { role: 'system', content: systemPromptFor(messages, options) },
        ...messages.map((message) => ({ role: message.author === 'bot' ? 'assistant' : 'user', content: toOpenAiContent(message) }) as OpenAiMessage),
      ],
      ...this.generationOptions(target, model, settings?.temperature ?? DEFAULT_SETTINGS.temperature, options.maxTokens ?? 2048),
      stream: true,
      ...(this.supportsReasoningEffort(model)
        ? { reasoning_effort: options.reasoningEffort ?? settings?.reasoningEffort ?? DEFAULT_SETTINGS.reasoningEffort }
        : {}),
    }, signal) : await fetch(endpoint, {
      method: 'POST',
      signal,
      headers: this.headersFor(target, true),
      body: JSON.stringify({
        model,
        messages: [
          { role: 'system', content: systemPromptFor(messages, options) },
          ...messages.map((message) => ({ role: message.author === 'bot' ? 'assistant' : 'user', content: toOpenAiContent(message) }) as OpenAiMessage),
        ],
        ...this.generationOptions(
          target,
          model,
          settings?.temperature ?? DEFAULT_SETTINGS.temperature,
          options.maxTokens ?? 2048,
        ),
        stream: true,
        ...(this.supportsReasoningEffort(model)
          ? { reasoning_effort: options.reasoningEffort ?? settings?.reasoningEffort ?? DEFAULT_SETTINGS.reasoningEffort }
          : {}),
      }),
    });

    if (!response.ok || !response.body) {
      throw new Error(`LM Studio streaming request failed (${response.status}).`);
    }

    const reader = response.body.getReader();
    // Abort cancels the stream so the read loop below terminates promptly.
    const onAbort = () => void reader.cancel();
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
    const decoder = new TextDecoder();
    let buffer = '';
    let content = '';
    let providedThinking = '';
    let reply: LmStudioReply = { text: '' };
    const consume = (line: string) => {
      if (!line.startsWith('data:')) return;
      const data = line.slice(5).trim();
      if (!data || data === '[DONE]') return;
      const delta = (JSON.parse(data) as LmStudioStreamDelta).choices?.[0]?.delta;
      if (!delta) return;
      const contentDelta = delta.content ?? '';
      const thinkingDelta = delta.reasoning_content ?? delta.reasoning ?? '';
      if (!contentDelta && !thinkingDelta) return;
      content += contentDelta;
      providedThinking += thinkingDelta;
      reply = this.separateThinking(content, providedThinking || undefined);
      onUpdate(reply);
    };

    try {
      while (true) {
        const { value, done } = await reader.read();
        buffer += decoder.decode(value, { stream: !done });
        const lines = buffer.split(/\r?\n/);
        buffer = lines.pop() ?? '';
        for (const line of lines) consume(line);
        if (done) break;
      }
      if (buffer) consume(buffer);
    } finally {
      signal?.removeEventListener('abort', onAbort);
    }
    if (!reply.text) throw new Error('LM Studio streaming response did not contain a final answer.');
    return reply;
  }

  private async resolveTarget(options: ChatOptions, signal?: AbortSignal): Promise<ApiTarget> {
    const settings = this.persistence.getEffectiveSettings();
    const helper = settings.helperModel;
    const separateHelper = options.useHelperApi && helper.enabled && !!helper.apiUrl?.trim();
    const apiUrl = separateHelper ? helper.apiUrl : settings.mainApiUrl;
    const isOpenAi = this.isOpenAiUrl(apiUrl);
    return this.resolveTargetForApi(
      apiUrl,
      isOpenAi && this.auth.isConfigured ? '' : environment.apiKey,
      signal,
      isOpenAi && this.auth.isConfigured,
    );
  }

  private async resolveTargetForApi(apiUrl?: string, apiKey = environment.apiKey, signal?: AbortSignal, proxyOpenAi = false): Promise<ApiTarget> {
    this.throwIfAborted(signal);
    const baseUrl = this.rootToBaseUrl(apiUrl || DEFAULT_SETTINGS.mainApiUrl);
    return {
      baseUrl,
      apiKey: apiKey?.trim() || undefined,
      modelIds: await this.fetchModels(baseUrl, apiKey, signal, proxyOpenAi),
      proxyOpenAi,
    };
  }

  private async fetchModels(baseUrl: string, apiKey?: string, signal?: AbortSignal, proxyOpenAi = false): Promise<string[]> {
    this.throwIfAborted(signal);
    if (proxyOpenAi) {
      const response = await this.proxyJson<LmStudioModelsResponse>('models', undefined, signal);
      return (response.data ?? []).map((model) => model.id);
    }
    const request$ = this.http.get<LmStudioModelsResponse>(this.endpoint(baseUrl, 'models'), {
      headers: this.headersFor({ apiKey }),
    });
    const response = await firstValueFrom(this.withAbort(request$, signal));
    return (response.data ?? []).map((model) => model.id);
  }

  /** Completes the request when the signal aborts so `firstValueFrom` rejects. */
  private withAbort<T>(request$: Observable<T>, signal?: AbortSignal): Observable<T> {
    if (!signal) return request$;
    if (signal.aborted) return throwError(() => signal.reason ?? new DOMException('Aborted', 'AbortError'));
    return request$.pipe(takeUntil(this.abortObservable(signal)));
  }

  private abortObservable(signal: AbortSignal): Observable<void> {
    return fromEvent(signal, 'abort').pipe(map(() => undefined), take(1));
  }

  /** Fails fast with an abort error when the caller has already cancelled. */
  private throwIfAborted(signal?: AbortSignal): void {
    if (signal?.aborted) throw signal.reason ?? new DOMException('Aborted', 'AbortError');
  }

  private modelForTarget(target: ApiTarget, configuredModel: string): string {
    // Use only a model the endpoint currently advertises. Routers may expose
    // an `auto` virtual model, while direct LM Studio endpoints require a
    // concrete installed model and saved settings can become stale.
    if (target.modelIds.some((model) => model.toLowerCase() === configuredModel.toLowerCase())) {
      return configuredModel;
    }

    // A router's virtual model is the safest recovery when a previously saved
    // concrete model is no longer installed.
    const autoModel = target.modelIds.find((model) => model.toLowerCase() === 'auto');
    if (autoModel) return autoModel;

    return target.modelIds.find((model) => /^gpt-4\.1-mini$/i.test(model))
      ?? target.modelIds.find((model) => /^gpt-5(?:\.\d+)?(?:-mini)?$/i.test(model))
      ?? target.modelIds.find((model) => !/embed/i.test(model))
      ?? target.modelIds[0]
      ?? configuredModel;
  }

  private rootToBaseUrl(rootUrl: string): string {
    const normalized = rootUrl.trim().replace(/\/+$/, '');

    // The default local server is proxied during development to avoid CORS.
    if (this.sameUrl(normalized, environment.lmStudioRootUrl)) {
      return environment.apiUrl.replace(/\/+$/, '');
    }
    return normalized;
  }

  private sameUrl(left: string, right: string): boolean {
    try {
      const a = new URL(left);
      const b = new URL(right);
      return a.protocol === b.protocol
        && a.hostname === b.hostname
        && a.port === b.port
        && a.pathname.replace(/\/+$/, '') === b.pathname.replace(/\/+$/, '');
    } catch {
      return left === right.replace(/\/+$/, '');
    }
  }

  private headersFor(target: Pick<ApiTarget, 'apiKey'>, streaming = false): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      Accept: streaming ? 'text/event-stream' : 'application/json',
      ...(target.apiKey?.trim() ? { Authorization: `Bearer ${target.apiKey.trim()}` } : {}),
    };
  }

  /** Supports both server roots and OpenAI-style URLs that already end in `/v1`. */
  private endpoint(baseUrl: string, path: string): string {
    const base = baseUrl.replace(/\/+$/, '');
    return `${base}${/\/v1$/i.test(base) ? '' : '/v1'}/${path}`;
  }

  private supportsReasoningEffort(model: string): boolean {
    return /^(?:gpt-5|o[1-4])(?:[.-]|$)/i.test(model);
  }

  /** Keeps legacy LM Studio payloads separate from newer OpenAI model requirements. */
  private generationOptions(target: ApiTarget, model: string, temperature: number, maxTokens: number): Record<string, number> {
    if (!this.isOpenAiTarget(target)) {
      return { temperature, max_tokens: maxTokens };
    }

    // GPT-5 models use the newer completion limit and only support their
    // default temperature. Other OpenAI chat models retain the legacy fields.
    if (/^gpt-5(?:[.-]|$)/i.test(model)) {
      return { max_completion_tokens: maxTokens };
    }
    return { temperature, max_tokens: maxTokens };
  }

  private isOpenAiTarget(target: ApiTarget): boolean {
    if (target.proxyOpenAi) return true;
    try {
      return new URL(target.baseUrl).hostname === 'api.openai.com';
    } catch {
      return false;
    }
  }

  private isOpenAiUrl(url?: string): boolean {
    try {
      return new URL(url ?? '').hostname === 'api.openai.com';
    } catch {
      return false;
    }
  }

  private async proxyJson<T>(path: 'models' | 'chat/completions', body?: Record<string, unknown>, signal?: AbortSignal, timeoutMs?: number): Promise<T> {
    const request = this.proxyFetch(path, body, signal);
    const response = timeoutMs
      ? await Promise.race([request, new Promise<Response>((_, reject) => setTimeout(() => reject(new Error('OpenAI request timed out.')), timeoutMs))])
      : await request;
    if (!response.ok) throw new Error(await this.proxyError(response));
    return response.json() as Promise<T>;
  }

  private async proxyFetch(path: 'models' | 'chat/completions', body?: Record<string, unknown>, signal?: AbortSignal): Promise<Response> {
    const url = this.auth.functionUrl('openai-proxy');
    const token = await this.auth.accessToken();
    if (!url || !token) throw new Error('Sign in to use your saved OpenAI API key.');
    return fetch(url, {
      method: 'POST',
      signal,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ path, ...(body ? { body } : {}) }),
    });
  }

  private async proxyError(response: Response): Promise<string> {
    try {
      const error = await response.json() as { error?: { message?: string } };
      return error.error?.message ?? `OpenAI proxy request failed (${response.status}).`;
    } catch {
      return `OpenAI proxy request failed (${response.status}).`;
    }
  }
}
